/* eslint-disable @typescript-eslint/only-throw-error */
import { PermissionAdminLevel, PermissionLevel } from '@/api/User/types.ts'
import type { AuthUser } from '@/hooks/useGetCurrentUser/types'
import { getBusinessQueryOptions } from '@/queries/Business'
import {
  fetchCollectAuditsQueryOptions,
  getCollectQueryOptions,
} from '@/queries/Collect'
import {
  getFeatureQueryOptions,
} from '@/queries/Feature'
import { getGroupQueryOptions } from '@/queries/Group'
import { getInitiativeQueryOptions } from '@/queries/Initiative'
import { getMeOptions, getUserQueryOptions } from '@/queries/User'
import type {
  PaginatedInitiativeSearchSchema,
  PaginatedOrderedQuerySearchSchema,
  PaginatedSearchSchema,
} from '@/routes/schemas'
import {
  enrichmentUserWithMethods,
  getCurrentUser,
  hasUserLocale,
  saveCurrentUser,
} from '@/utils/auth.util'
import isLanguageSupported from '@/utils/isLanguageSupported.ts'
import queryClient from '@/utils/queryClient.ts'
import {
  createRootRouteWithContext,
  createRoute,
  notFound,
  redirect,
} from '@tanstack/react-router'
import { lazy } from 'react'
import { withAuthenticationRequired } from 'react-oidc-context'

const CollectView = lazy(() => import('@/pages/Collects/CreateEdit/View'))
const CollectEdit = lazy(() => import('@/pages/Collects/CreateEdit/Edit'))
const CollectAuditList = lazy(() => import('@/pages/Collects/AuditList'))
const AuditView = lazy(() => import('@/pages/Collects/AuditView'))
const InitiativeDuplicate = lazy(() => import('@/pages/Initiatives/Duplicate'))
const CollectList = lazy(() => import('@/pages/Collects/List'))
const CollectCreate = lazy(() => import('@/pages/Collects/CreateEdit/Create'))
const Loading = lazy(() => import('@/components/Loading'))
const BusinessCreate = lazy(
  () => import('@/pages/Businesses/CreateEdit/Create')
)
const BusinessEdit = lazy(() => import('@/pages/Businesses/CreateEdit/Edit'))
const BusinessList = lazy(() => import('@/pages/Businesses/List'))
const ConstructorFieldsLayout = lazy(
  () => import('@/pages/Constructor/CreateEdit')
)
const FeatureCreate = lazy(() => import('@/pages/Features/CreateEdit/Create'))
const FeatureEdit = lazy(() => import('@/pages/Features/CreateEdit/Edit'))
const FeatureAnalytics = lazy(() => import('@/pages/Features/Analytics'))
const FeaturesDuplicate = lazy(() => import('@/pages/Features/Duplicate'))
const GroupCreate = lazy(() => import('@/pages/Groups/CreateEdit/Create'))
const GroupEdit = lazy(() => import('@/pages/Groups/CreateEdit/Edit'))
const GroupList = lazy(() => import('@/pages/Groups/List'))
const InitiativeCreate = lazy(
  () => import('@/pages/Initiatives/CreateEdit/Create')
)
const InitiativeEdit = lazy(() => import('@/pages/Initiatives/CreateEdit/Edit'))
const InitiativeList = lazy(() => import('@/pages/Initiatives/List'))
const InitiativeView = lazy(() => import('@/pages/Initiatives/View'))
const NotAuthorized = lazy(() => import('@/pages/NotAuthorized'))
const UserCreate = lazy(() => import('@/pages/Users/CreateEdit/Create'))
const UserEdit = lazy(() => import('@/pages/Users/CreateEdit/Edit'))
const UserList = lazy(() => import('@/pages/Users/List'))
const Layout = lazy(() => import('@/pages/Layout/layout.tsx'))
const ImportDataView = lazy(() => import('@/pages/ImportedData/Create'))
const ExportDataView = lazy(() => import('@/pages/ExportData/Create'))
const SelfAppliedCollect = lazy(
  () => import('@/pages/Collects/CreateEdit/SelfApplied')
)
const SelfAppliedCollectFinished = lazy(
  () => import('@/pages/Collects/CreateEdit/SelfApplied/Finished/index.tsx')
)
const Login = lazy(() => import('@/pages/Login'))

function redirectTo(to?: string) {
  throw redirect({
    to: to,
    params: {
      locale: isLanguageSupported(navigator.language)
        ? navigator.language
        : 'en',
    } as unknown as true,
  })
}

export const Route = createRootRouteWithContext<object>()({
  component: Layout,
})

export const LoginRoute = createRoute({
  path: '/login',
  getParentRoute: () => Route,
  component: Login,
  staticData: { noLayout: true },
})

export const HomeRoute = createRoute({
  path: '/',
  getParentRoute: () => Route,
  loader: async () => {
    let user
    if (hasUserLocale()) {
      user = getCurrentUser()
    } else {
      const result = await queryClient.ensureQueryData(getMeOptions())
      user = enrichmentUserWithMethods(result)
      saveCurrentUser(result)
    }

    if (
      user?.hasPermission(PermissionLevel.READ) ||
      (user?.initiatives != null && Object.keys(user.initiatives).length > 0)
    ) {
      redirectTo(InitiativesRoute.to)
    } else if (
      user?.hasAdminPermission('business', PermissionAdminLevel.READ)
    ) {
      redirectTo(BusinessesRoute.to)
    } else if (user?.hasAdminPermission('user', PermissionAdminLevel.READ)) {
      redirectTo(UsersRoute.to)
    }
    redirectTo(PrivateRoute.to)
  },
})

export const LocaleRoute = createRoute({
  path: '$locale/',
  getParentRoute: () => Route,
  beforeLoad: ({ params, location }) => {
    const pathname = location.pathname

    if (params.locale === null || !isLanguageSupported(params.locale)) {
      redirectTo()
    }

    const localePattern = new RegExp(`^/${params.locale}/?$`)

    if (pathname.match(localePattern)) {
      redirectTo(InitiativesRoute.to)
    }
  },
})

export const PrivateRoute = createRoute({
  id: 'private',
  getParentRoute: () => LocaleRoute,
  beforeLoad: async () => {
    try {
      let currentUser: AuthUser | undefined
      if (hasUserLocale()) {
        currentUser = getCurrentUser()
      } else {
        const result = await queryClient.ensureQueryData(getMeOptions())
        currentUser = enrichmentUserWithMethods(result)
        saveCurrentUser(result)
      }

      return { user: currentUser }
    } catch {
      redirectTo(LoginRoute.to)
    }
  },
  staleTime: 0,
})

export const BusinessesRoute = createRoute({
  path: 'negocios',
  getParentRoute: () => PrivateRoute,
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('business', PermissionAdminLevel.READ)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: () => {
    return { crumb: 'Negócios' }
  },
})

export const BusinessesListRoute = createRoute({
  path: '/',
  getParentRoute: () => BusinessesRoute,
  component: withAuthenticationRequired(BusinessList),
  validateSearch: (search: Record<string, unknown>): PaginatedSearchSchema => {
    return {
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 9),
    }
  },
})

export const BusinessesCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => BusinessesRoute,
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('business', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  component: withAuthenticationRequired(BusinessCreate),
})

export const BusinessesEditRoute = createRoute({
  path: '$businessId/editar',
  getParentRoute: () => BusinessesRoute,
  component: withAuthenticationRequired(BusinessEdit),
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('business', PermissionAdminLevel.ADMIN)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { businessId } }) => {
    try {
      await queryClient.ensureQueryData(getBusinessQueryOptions(businessId))
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const InitiativesRoute = createRoute({
  path: 'iniciativas',
  getParentRoute: () => PrivateRoute,
  beforeLoad: () => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(PermissionLevel.READ) &&
      user?.initiatives &&
      Object.keys(user.initiatives).length === 0
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: () => {
    return { crumb: 'Iniciativas' }
  },
})

export const InitiativesListRoute = createRoute({
  path: '/',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeList),
  validateSearch: (
    search: Record<string, unknown>
  ): PaginatedInitiativeSearchSchema => {
    return {
      filter: (search.filter as string) || '',
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 12),
      business: (search.business as string) || '',
    }
  },
})

export const InitiativesEditRoute = createRoute({
  path: '$initiativeId/editar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeEdit),
  beforeLoad: ({ params: { initiativeId } }) => {
    const user = getCurrentUser()
    if (!user?.hasPermission(PermissionLevel.ADMIN, Number(initiativeId))) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const InitiativesViewRoute = createRoute({
  path: '$initiativeId/visualizar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeView),
  beforeLoad: ({ params: { initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !(
        user?.hasPermission(PermissionLevel.READ, Number(initiativeId)) ||
        (user &&
          Object.keys(user.initiatives[Number(initiativeId)].features).length >
            0)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const InitiativesCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeCreate),
  beforeLoad: async ({ search }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('initiative', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to)
      return
    }

    if ('businessId' in search) {
      try {
        await queryClient.ensureQueryData(
          getBusinessQueryOptions(Number(search.businessId))
        )
      } catch {
        throw notFound()
      }
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const UsersRoute = createRoute({
  path: 'usuarios',
  getParentRoute: () => PrivateRoute,
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.READ)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: () => {
    return { crumb: 'Usuários' }
  },
})

export const UsersListRoute = createRoute({
  path: '/',
  getParentRoute: () => UsersRoute,
  component: withAuthenticationRequired(UserList),
  validateSearch: (
    search: Record<string, unknown>
  ): PaginatedOrderedQuerySearchSchema => {
    return {
      filter: (search.filter as string) || '',
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
      orderBy: 'createdAt',
      orderSort: 'DESC',
    }
  },
})

export const UsersEditRoute = createRoute({
  path: '$userId/editar',
  getParentRoute: () => UsersRoute,
  component: withAuthenticationRequired(UserEdit),
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.ADMIN)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { userId } }) => {
    try {
      await queryClient.ensureQueryData(getUserQueryOptions(userId))
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const UsersEditPermissionsRoute = createRoute({
  path: '$userId/permissoes',
  getParentRoute: () => UsersRoute,
  loader: async ({ params: { userId } }) => {
    try {
      await queryClient.ensureQueryData(getUserQueryOptions(userId))
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const UsersCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => UsersRoute,
  component: withAuthenticationRequired(UserCreate),
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
})

export const GroupsRoute = createRoute({
  path: 'grupos',
  getParentRoute: () => PrivateRoute,
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.READ)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
})

export const GroupsListRoute = createRoute({
  path: '/',
  getParentRoute: () => GroupsRoute,
  component: withAuthenticationRequired(GroupList),
  validateSearch: (
    search: Record<string, unknown>
  ): PaginatedOrderedQuerySearchSchema => {
    return {
      filter: (search.filter as string) || '',
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
      orderBy: 'name',
      orderSort: 'DESC',
    }
  },
})

export const GroupsEditRoute = createRoute({
  path: '$groupId/editar',
  getParentRoute: () => GroupsRoute,
  component: withAuthenticationRequired(GroupEdit),
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.ADMIN)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { groupId } }) => {
    try {
      await queryClient.ensureQueryData(getGroupQueryOptions(groupId))
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const GroupsCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => GroupsRoute,
  component: withAuthenticationRequired(GroupCreate),
  beforeLoad: () => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
})

export const FeaturesRoute = createRoute({
  path: '$initiativeId/funcionalidades',
  getParentRoute: () => InitiativesRoute,
  loader: () => {
    return { crumb: 'Funcionalidades' }
  },
  beforeLoad: ({ params: { initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !(
        user?.hasPermission(PermissionLevel.READ, Number(initiativeId)) ||
        (user &&
          Object.keys(user.initiatives[Number(initiativeId)].features).length >
            0)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
})

export const FeaturesCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeatureCreate),
})

export const FeaturesEditRoute = createRoute({
  path: '$featureId',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeatureEdit),
  beforeLoad: ({ params: { featureId, initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.ADMIN,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const FeaturesAnalyticsRoute = createRoute({
  path: '$featureId/estatisticas',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeatureAnalytics),
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
  },
  beforeLoad: ({ params: { featureId, initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.ADMIN,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const FeaturesDuplicateRoute = createRoute({
  path: '$featureId/duplicar',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeaturesDuplicate),
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
  },
  beforeLoad: ({ params: { featureId, initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.WRITE,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const CollectsRoute = createRoute({
  path: '$featureId/coletas',
  getParentRoute: () => FeaturesRoute,
  beforeLoad: ({ params: { featureId, initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.LOOKUP,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
})

export const CollectsListRoute = createRoute({
  path: '/',
  getParentRoute: () => CollectsRoute,
  validateSearch: (search: Record<string, unknown>): PaginatedSearchSchema => {
    return {
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
    }
  },
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
    return { crumb: 'Coletas' }
  },
  component: withAuthenticationRequired(CollectList),
})

export const CollectsCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectCreate),
  beforeLoad: ({ params: { featureId, initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.COLLECTOR,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
    return { crumb: 'Coletas' }
  },
})

export const CollectsEditRoute = createRoute({
  path: '$collectId/editar',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectEdit),
  pendingComponent: Loading,
  pendingMs: 0,
  beforeLoad: ({ params: { featureId, initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.ADMIN,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  loader: async ({ params: { initiativeId, featureId, collectId } }) => {
    try {
      await queryClient.ensureQueryData(
        getCollectQueryOptions(initiativeId, featureId, 'shared', collectId)
      )
    } catch {
      throw notFound()
    }
  },
})

export const CollectsViewRoute = createRoute({
  path: '$collectId/visualizar',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectView),
  pendingComponent: Loading,
  pendingMs: 0,
  loader: async ({ params: { initiativeId, featureId, collectId } }) => {
    try {
      await queryClient.ensureQueryData(
        getCollectQueryOptions(initiativeId, featureId, 'shared', collectId)
      )
    } catch {
      throw notFound()
    }
  },
})

export const CollectAuditsListRoute = createRoute({
  path: '$collectId/auditoria',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectAuditList),
  loader: async (loaderContext) => {
    const { initiativeId, featureId, collectId } = loaderContext.params
    const { page, limit } = loaderContext.location.search as {
      page: number
      limit: number
    }
    try {
      await queryClient.ensureQueryData(
        fetchCollectAuditsQueryOptions(initiativeId, featureId, collectId, {
          page,
          limit,
        })
      )
    } catch {
      throw notFound()
    }
  },
  validateSearch: (search: Record<string, unknown>): PaginatedSearchSchema => {
    return {
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const CollectAuditsViewRoute = createRoute({
  path: '$collectId/auditoria/$version',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(AuditView),
  pendingComponent: Loading,
  pendingMs: 0,
  loader: async ({
    params: { initiativeId, featureId, collectId, version },
  }) => {
    try {
      await queryClient.ensureQueryData(
        getCollectQueryOptions(
          initiativeId,
          featureId,
          'shared',
          collectId,
          version
        )
      )
    } catch {
      throw notFound()
    }
  },
})

export const NotAuthorizedRoute = createRoute({
  path: '/acesso-negado',
  getParentRoute: () => LocaleRoute,
  component: NotAuthorized,
  staticData: { noLayout: true },
})

export const FeatureConstructorForm = createRoute({
  path: '$featureId/construtor',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(ConstructorFieldsLayout),
  staticData: { noLayout: true },
  beforeLoad: ({ params: { featureId, initiativeId } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.ADMIN,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
})

export const ImportedDataRoute = createRoute({
  path: '$initiativeId/importar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(ImportDataView),
  loader: async ({ params: { initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
    } catch {
      throw notFound()
    }
  },
  beforeLoad: ({ params: { initiativeId } }) => {
    const user = getCurrentUser()

    if (!user?.hasPermission(PermissionLevel.ADMIN, Number(initiativeId))) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const ExportDataRoute = createRoute({
  path: '$initiativeId/exportar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(ExportDataView),
  loader: async ({ params: { initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
    } catch {
      throw notFound()
    }
  },
  beforeLoad: ({ params: { initiativeId } }) => {
    const user = getCurrentUser()

    if (!user?.hasPermission(PermissionLevel.ADMIN, Number(initiativeId))) {
      redirectTo(NotAuthorizedRoute.to)
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})

export const SelfAppliedCollectRedirectRoute = createRoute({
  path: 'collect/$key',
  getParentRoute: () => Route,
  beforeLoad: () => {
    redirectTo(SelfAppliedCollectRoute.to)
  },
})

export const SelfAppliedCollectRoute = createRoute({
  path: 'collect/$key',
  getParentRoute: () => LocaleRoute,
  component: SelfAppliedCollect,
  staticData: { noLayout: true },
})

export const SelfAppliedCollectFinishedRoute = createRoute({
  path: 'collect/$key/finished',
  getParentRoute: () => LocaleRoute,
  component: SelfAppliedCollectFinished,
  staticData: { noLayout: true },
})

export const InitiativesDuplicateRoute = createRoute({
  path: '$initiativeId/duplicar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeDuplicate),
  loader: async ({ params: { initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
    } catch {
      throw notFound()
    }
  },
  pendingComponent: Loading,
  pendingMs: 0,
})
